import React from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, SectionCard, Modal, StatusBadge, EmptyState, CardSkeleton, DateCell, ConfirmModal } from "@/admin/ui";
import { Plus } from "lucide-react";

export default function AdminIncidents() {
  const [incidents, setIncidents] = React.useState(null);
  const [showForm, setShowForm] = React.useState(false);
  const [resolveId, setResolveId] = React.useState(null);
  const [form, setForm] = React.useState({ title: "", body: "", severity: "minor", status: "investigating", affected_services: "" });

  const load = () => api.get("/api/admin/incidents").then((r) => setIncidents(r.data.items));
  React.useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/admin/incidents", { ...form, affected_services: form.affected_services.split(",").map((x) => x.trim()).filter(Boolean) });
      toast.success("Incident published");
      setForm({ title: "", body: "", severity: "minor", status: "investigating", affected_services: "" });
      setShowForm(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const doResolve = async () => {
    await api.put(`/api/admin/incidents/${resolveId}`, { status: "resolved" });
    toast.success("Updated"); load();
  };

  return (
    <div className="space-y-6" data-testid="admin-incidents-page">
      <PageHeader title="Incidents & maintenance" description="Publish updates to the public status page."
        primary={<button className="lp-btn-primary text-xs py-1.5 px-3" onClick={() => setShowForm(true)} data-testid="new-incident-btn">
          <Plus className="w-3.5 h-3.5" /> New incident
        </button>} />

      {!incidents ? <CardSkeleton h="h-40" /> : incidents.length === 0 ? (
        <EmptyState title="No incidents on file." />
      ) : (
        <div className="space-y-3">
          {incidents.map((i) => (
            <SectionCard key={i.id} testid={`incident-${i.id}`}
              title={<span className="flex items-center gap-2 flex-wrap">
                {i.title}
                <StatusBadge tone={i.status === "resolved" ? "success" : "warning"}>{i.status}</StatusBadge>
                <StatusBadge tone={i.severity === "critical" ? "danger" : i.severity === "major" ? "warning" : "neutral"}>{i.severity}</StatusBadge>
              </span>}>
              <p className="text-white/60 text-sm">{i.body}</p>
              <div className="text-xs text-white/40"><DateCell value={i.created_at} /></div>
              {i.status !== "resolved" && (
                <button onClick={() => setResolveId(i.id)} className="lp-btn-secondary text-xs py-1.5 px-3" data-testid={`resolve-${i.id}`}>Resolve</button>
              )}
            </SectionCard>
          ))}
        </div>
      )}

      <Modal open={showForm} onClose={() => setShowForm(false)} title="New incident" size="lg"
        footer={<>
          <button className="lp-btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
          <button className="lp-btn-primary" onClick={create} data-testid="incident-submit">Publish</button>
        </>}>
        <form onSubmit={create} className="grid md:grid-cols-2 gap-3" data-testid="incident-form">
          <input required className="lp-input" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="incident-title" data-autofocus />
          <input className="lp-input" placeholder="Affected services (comma-sep slugs)" value={form.affected_services} onChange={(e) => setForm({ ...form, affected_services: e.target.value })} data-testid="incident-affected" />
          <select className="lp-input" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
            {["minor", "major", "critical", "maintenance"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="lp-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {["investigating", "identified", "monitoring", "resolved"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <textarea className="lp-input md:col-span-2 min-h-[80px]" placeholder="Update body…" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
        </form>
      </Modal>

      <ConfirmModal open={!!resolveId} onClose={() => setResolveId(null)} onConfirm={doResolve}
        title="Resolve incident" description="Mark this incident as resolved on the public status page." actionLabel="Resolve" />
    </div>
  );
}
